import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import Price from "../../common/Price";
import { PromotionCodeList } from "../../../types/admin";

interface PromotionCodeTableProps {
  codes: PromotionCodeList[];
  loading?: boolean;
  onEdit: (code: PromotionCodeList) => void;
  onDelete: (code: PromotionCodeList) => void;
  onView: (code: PromotionCodeList) => void;
}

const PromotionCodeTable: React.FC<PromotionCodeTableProps> = ({
  codes,
  loading,
  onEdit,
  onDelete,
  onView,
}) => {
  const { t } = useTranslation();
  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("admin.promotionCodes.code")}</TableCell>
              <TableCell>{t("admin.promotionCodes.description")}</TableCell>
              <TableCell>{t("admin.promotionCodes.status")}</TableCell>
              <TableCell>{t("admin.promotionCodes.totalUses")}</TableCell>
              <TableCell>{t("admin.promotionCodes.uniqueUsers")}</TableCell>
              <TableCell>{t("admin.promotionCodes.totalDiscount")}</TableCell>
              <TableCell>{t("admin.promotionCodes.remainingUses")}</TableCell>
              <TableCell>{t("admin.promotionCodes.expires")}</TableCell>
              <TableCell align="right">{t("admin.promotionCodes.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={28} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : codes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.promotionCodes.noCodesFound")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              codes.map((code) => (
                <TableRow key={code.id} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ fontFamily: "monospace" }}
                    >
                      {code.code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={code.description}>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {code.description}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={code.isActive ? t("admin.promotionCodes.active") : t("admin.promotionCodes.inactive")}
                      color={code.isActive ? "success" : "default"}
                      size="small"
                    />
                    {code.isExpired && (
                      <Chip
                        label={t("admin.promotionCodes.expired")}
                        color="error"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{code.totalUses}</TableCell>
                  <TableCell>{code.uniqueUsers}</TableCell>
                  <TableCell>
                    <Price value={code.totalDiscountGiven} disableCentsHeuristic />
                  </TableCell>
                  <TableCell>
                    {code.maxTotalUses
                      ? `${code.remainingUses} / ${code.maxTotalUses}`
                      : t("admin.promotionCodes.unlimited")}
                  </TableCell>
                  <TableCell>
                    {code.endDate
                      ? new Date(code.endDate).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t("admin.promotionCodes.viewDetails")}>
                      <IconButton
                        size="small"
                        onClick={() => onView(code)}
                        color="primary"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("admin.promotionCodes.edit")}>
                      <IconButton
                        size="small"
                        onClick={() => onEdit(code)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("admin.promotionCodes.delete")}>
                      <IconButton
                        size="small"
                        onClick={() => onDelete(code)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PromotionCodeTable;
